/**
 * Admission charge-ledger API (EPIC C / C3) — 1:1 with the frozen §2 API
 * CONTRACT (state/epics/C/C3/build-plan.md). Routes mount under the gated IPD
 * router (`/api/organisation`, `require_admission_enabled` → 403 ADMISSION_DISABLED
 * for dark orgs). Envelope = bare Pydantic models; errors = `detail:{code,message}`.
 * ALL money fields are integer paise (signed).
 *
 * Each function returns `{ success, data?, error?, code?, ...extras }`; `code`
 * carries the machine-readable business-rule code so the caller can branch
 * (409 ALREADY_REVERSED → treat as done, 422 SERVICE_DATE_OUT_OF_WINDOW → inline).
 */

import apiClient from './apiClient';

const BASE = '/api/organisation/admissions';
const enc = encodeURIComponent;

/** Same IPD error-shape translation the admission API uses (code + extras pass through). */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  if (typeof detail === 'string') return { error: detail };
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to view this bill.' };
    case 404: return { error: 'Admission not found.' };
    case 422: return { error: fallback, code: 'VALIDATION_ERROR' };
    case 503: return { error: 'Admissions are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    default:  return { error: fallback };
  }
}

/** C-1 GET /admissions/{id}/running-bill — RunningBillResponse (charges newest-first + totals). */
export const getRunningBill = async (admissionId) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/running-bill`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ChargesAPI] getRunningBill error:', error);
    return { success: false, ...parseError(error, 'Failed to load the running bill.') };
  }
};

/**
 * C-2 POST /admissions/{id}/charges — add a manual (ad-hoc) charge. The SERVER
 * computes the amount; the body carries only description/qty/unit_price/gst plus
 * a REQUIRED idempotency_key (never a client-computed total → 422 on extra fields).
 * A 201 (created) and a 200 (idempotent replay) are BOTH success.
 */
export const addCharge = async (admissionId, body) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/charges`, {
      description: body.description,
      unit_price_paise: body.unit_price_paise,
      qty: body.qty,
      gst_rate: body.gst_rate,
      service_date: body.service_date,
      idempotency_key: body.idempotency_key,
    });
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[ChargesAPI] addCharge error:', error);
    return { success: false, ...parseError(error, 'Failed to add the charge.') };
  }
};

/**
 * C-3 POST /admissions/{id}/charges/{chargeId}/reverse — reverse a POSTED line.
 * Posts a linked negative REVERSAL row (server computes −original). A 409
 * ALREADY_REVERSED means someone already reversed it — the caller treats that as
 * done and refreshes rather than erroring.
 */
export const reverseCharge = async (admissionId, chargeId, { reason } = {}) => {
  try {
    const response = await apiClient.post(
      `${BASE}/${enc(admissionId)}/charges/${enc(chargeId)}/reverse`,
      { reason },
    );
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[ChargesAPI] reverseCharge error:', error);
    return { success: false, ...parseError(error, 'Failed to reverse the charge.') };
  }
};

/**
 * §2.2 POST /admissions/{id}/item-charges (day-1 S2, frozen contract) — post a
 * server-priced line from the Charge Items master. The client sends ONLY
 * `item_id` + `qty` (+ optional `service_date`) + `idempotency_key` — NEVER a
 * rate; the server loads the item, recomputes base+GST from its saved price,
 * and stamps `kind:"ITEM"`. A 201 (created) and a 200 (idempotent replay) are
 * BOTH success.
 *
 * `unit_price_paise` (OPD-CHARGE-RANGE §1.5, additive) — the desk's TYPED
 * amount for a variable-price item, already `Math.round(₹ * 100)` by the
 * caller. Omitted for a fixed item (server prices it); the server 422s
 * `PRICE_OUT_OF_RANGE` if sent for a fixed item or missing/out-of-range for
 * a variable one.
 *
 * Envelope: ChargePostResponse = { charge, balance_paise, replayed }
 */
export const addItemCharge = async (admissionId, { item_id, qty, service_date, idempotency_key, unit_price_paise } = {}) => {
  try {
    const body = { item_id, qty, idempotency_key };
    if (service_date) body.service_date = service_date;
    if (unit_price_paise != null) body.unit_price_paise = unit_price_paise;
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/item-charges`, body);
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[ChargesAPI] addItemCharge error:', error);
    return { success: false, ...parseError(error, 'Failed to add the charge.') };
  }
};

/**
 * NEW POST /admissions/{id}/concessions — grant a bill-side waiver/concession
 * (C4 defect fix, frozen contract). ADMIN only + mandatory reason (server-
 * enforced). The SERVER caps the amount at net POSTED charges; an over-ask
 * returns 422 CONCESSION_EXCEEDS_CHARGES + `remaining_paise` (the refreshed
 * cap) so the loser of a race sees the true headroom rather than a silent
 * success. A 201 (created) and a 200 (idempotent replay) are BOTH success.
 *
 * Envelope: { charge, balance_paise, voucher_number, replayed }
 */
export const grantConcession = async (admissionId, { amount_paise, reason, idempotency_key } = {}) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/concessions`, {
      amount_paise,
      reason,
      idempotency_key,
    });
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[ChargesAPI] grantConcession error:', error);
    return { success: false, ...parseError(error, 'Could not record the waiver.') };
  }
};

/**
 * C-4 GET /accrual-runs?limit=N — the org's bed-day accrual JobRun rows,
 * newest first. Deliberately OUTSIDE /admissions/... to avoid a route collision.
 */
export const listAccrualRuns = async ({ limit } = {}) => {
  try {
    const params = {};
    if (limit != null && Number.isFinite(limit)) params.limit = limit;
    const response = await apiClient.get('/api/organisation/accrual-runs', { params });
    return {
      success: true,
      data: {
        runs: response.data?.runs || [],
        count: response.data?.count ?? (response.data?.runs?.length || 0),
      },
    };
  } catch (error) {
    console.error('[ChargesAPI] listAccrualRuns error:', error);
    return { success: false, ...parseError(error, 'Failed to load accrual runs.') };
  }
};

const admissionChargesApi = {
  getRunningBill,
  addCharge,
  addItemCharge,
  reverseCharge,
  grantConcession,
  listAccrualRuns,
};

export default admissionChargesApi;
